// Профилната лента най-долу в менюто: Обнови · QR · Език · Цял екран · Изход.
// Разпънато — на един ред с бутон „Изход"; свито (рейл) — икони една под друга.
import { useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FullscreenExitOutlined,
  FullscreenOutlined,
  GlobalOutlined,
  LogoutOutlined,
  QrcodeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { logout, setAccess } from "../../../../store/authSlice";
import { denyAccess } from "../../../../api/access";
import { LanguageModal, QrModal } from "../../../../components/ProfileDropdown";
import {
  useFullscreen,
  useProfileLogout,
} from "../../../../components/profileActions";

export const SidebarProfileBar = ({ collapsed }: { collapsed: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const qc = useQueryClient();
  const access = useAppSelector((s) => s.auth.access);
  const [langOpen, setLangOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const fullscreen = useFullscreen();

  const handleLogout = async () => {
    if (access) {
      try {
        await denyAccess();
      } catch (e) {
        console.error("Deny access error:", e);
      }
      dispatch(setAccess(false));
      navigate("/", { replace: true });
    } else {
      await dispatch(logout());
      navigate("/login", { replace: true });
    }
  };
  const doLogout = useProfileLogout(access, handleLogout);
  const logoutLabel = access ? t("modules.revokeAccess") : t("common.logout");

  const icons = (
    <>
      <IconButton
        title={t("inventoryPage.refresh")}
        onClick={() => qc.invalidateQueries({ queryKey: ["inventory"] })}
      >
        <ReloadOutlined className="text-[13px]" />
      </IconButton>
      <IconButton title={t("common.qrTitle")} onClick={() => setQrOpen(true)}>
        <QrcodeOutlined className="text-[14px]" />
      </IconButton>
      <IconButton title={t("common.language")} onClick={() => setLangOpen(true)}>
        <GlobalOutlined className="text-[14px]" />
      </IconButton>
      <IconButton
        title={
          fullscreen.isFullscreen
            ? t("common.exitFullscreen")
            : t("common.fullscreen")
        }
        onClick={fullscreen.toggle}
      >
        {fullscreen.isFullscreen ? (
          <FullscreenExitOutlined className="text-[14px]" />
        ) : (
          <FullscreenOutlined className="text-[14px]" />
        )}
      </IconButton>
    </>
  );

  return (
    <div className="mt-auto border-t border-shade-2 pt-2">
      {collapsed ? (
        <div className="flex flex-col items-center gap-0.5 px-0 pb-0.5 pt-1.5">
          {icons}
          <IconButton title={logoutLabel} onClick={() => void doLogout()} accent>
            <LogoutOutlined className="text-[14px]" />
          </IconButton>
        </div>
      ) : (
        <div className="flex items-center gap-1 px-2.5 pb-1 pt-0.5">
          {icons}
          {/* Изход — ghost бутон в кехлибарената гама, вдясно на същия ред */}
          <button
            type="button"
            onClick={() => void doLogout()}
            className="ml-auto flex h-7 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-[7px] border border-brand/35 bg-transparent px-2.5 text-xs font-semibold leading-[normal] text-brand-deep-ink transition-colors hover:border-brand/55 hover:bg-brand-tint"
          >
            <LogoutOutlined className="text-xs" />
            {t("common.logout")}
          </button>
        </div>
      )}
      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} />
      <QrModal open={qrOpen} onClose={() => setQrOpen(false)} />
    </div>
  );
};

// Малка икон-кутийка за профилната лента
function IconButton({
  title,
  onClick,
  accent,
  children,
}: {
  title: string;
  onClick: () => void;
  // Изход — брандовата кехлибарена гама (не червено)
  accent?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent transition-colors ${
        accent
          ? "text-brand-deep-ink hover:bg-brand-tint"
          : "text-muted hover:bg-shade-1 hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

export default SidebarProfileBar;
